import React from "react";
import { Tooltip } from "antd";
import DateRangePicker from "../../../components/Charts/DateRangePicker";
import {
  FiRefreshCw,
  FiDownload,
  FiFileText,
  FiCalendar,
} from "react-icons/fi";
import { IoStatsChart } from "react-icons/io5";
import moment from "moment";

const AnalyticsHeader = ({
  activeFilterTab,
  handleQuickDateChange,
  dateRange,
  setDateRange,
  setActiveFilterTab,
  fetchAnalyticsData,
  isLoading,
  handleExportCSV,
  handleExportPDF,
  isExportingPDF,
  analyticsData,
}) => {
  const presets = [
    { id: "today", label: "Today" },
    { id: "7d", label: "7D" },
    { id: "30d", label: "30D" },
    { id: "90d", label: "90D" },
    { id: "ytd", label: "YTD" },
  ];

  const dateLabel =
    activeFilterTab === "custom"
      ? `${moment(dateRange.startDate).format("MMM D")} – ${moment(dateRange.endDate).format("MMM D, YYYY")}`
      : activeFilterTab === "today"
        ? "Today"
        : activeFilterTab === "7d"
          ? "Last 7 Days"
          : activeFilterTab === "30d"
            ? "Last 30 Days"
            : activeFilterTab === "90d"
              ? "Last 90 Days"
              : "Year to Date";

  return (
    <div className="bg-white border border-gray-100 rounded-[20px] shadow-sm relative z-30">
      {/* Top stripe accent */}
      <div className="h-1 w-full bg-gradient-to-r from-[#CC9900] via-[#F0C040] to-[#CC9900] rounded-t-[20px]" />

      <div className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-5">
          {/* Left: Title + summary */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#CC9900]/20 to-[#CC9900]/5 flex items-center justify-center text-[#CC9900] border border-[#CC9900]/15 flex-shrink-0">
              <IoStatsChart className="text-2xl" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-x-2.5">
                <h1 className="text-lg sm:text-2xl font-extrabold text-[#111827] tracking-tight">
                  Analytics Dashboard
                </h1>
                <span
                  style={{
                    display: "inline-table",
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: "#ECFDF5",
                    color: "#047857",
                    border: "1px solid rgba(16,185,129,0.2)",
                    height: 22,
                    boxSizing: "border-box",
                    verticalAlign: "middle",
                  }}
                >
                  <span style={{ display: "table-row" }}>
                    <span
                      style={{
                        display: "table-cell",
                        verticalAlign: "middle",
                        paddingRight: 6,
                      }}
                    >
                      <span
                        className="pulse-dot"
                        style={{ display: "block", margin: "0 auto" }}
                      />
                    </span>
                    <span
                      style={{
                        display: "table-cell",
                        verticalAlign: "middle",
                        lineHeight: 1,
                      }}
                    >
                      Live
                    </span>
                  </span>
                </span>
              </div>
              {/* Subtitle row — all items in one flex line */}
              <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-1">
                <FiCalendar className="text-gray-400 text-[11px] flex-shrink-0" />
                <span className="text-xs text-gray-500 font-semibold">
                  {dateLabel}
                </span>
                {analyticsData && (
                  <>
                    <span className="text-gray-300 text-xs leading-none">
                      ·
                    </span>
                    <span className="text-xs text-gray-400">
                      <span className="font-bold text-[#111827]">
                        {analyticsData.totalUsers?.toLocaleString()}
                      </span>{" "}
                      users across{" "}
                      <span className="font-bold text-[#111827]">
                        {analyticsData.activeVenuesCount}
                      </span>{" "}
                      venues
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right: Controls */}
          <div className="flex flex-wrap items-center gap-2.5 no-pdf-export">
            {/* Quick presets */}
            <div className="flex items-center gap-0.5 sm:gap-1 bg-gray-100/80 p-1 rounded-xl border border-gray-200/50 max-w-full overflow-x-auto">
              {presets.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleQuickDateChange(p.id)}
                  className={`px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-bold rounded-lg transition-all duration-200 whitespace-nowrap shrink-0 ${
                    activeFilterTab === p.id
                      ? "bg-white text-[#CC9900] shadow-sm"
                      : "text-gray-500 hover:text-gray-800 hover:bg-white/60"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Custom date picker */}
            <div className="relative w-full sm:w-auto">
              <DateRangePicker
                ranges={dateRange}
                onChange={(range) => {
                  setActiveFilterTab("custom");
                  setDateRange({ ...range, key: "selection" });
                }}
              />
            </div>

            {/* Refresh */}
            <Tooltip title="Refresh data">
              <button
                onClick={fetchAnalyticsData}
                disabled={isLoading}
                className="w-9 h-9 shrink-0 rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-[#CC9900] hover:border-[#CC9900]/30 hover:bg-[#CC9900]/5 transition-all flex items-center justify-center"
              >
                <FiRefreshCw
                  className={`text-sm ${
                    isLoading ? "animate-spin text-[#CC9900]" : ""
                  }`}
                />
              </button>
            </Tooltip>

            {/* <div className="w-px h-6 bg-gray-200" /> */}
            {/* <Tooltip title="Export table as CSV">
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-600 font-semibold text-xs hover:bg-gray-50 hover:border-gray-300 active:scale-95 transition-all"
              >
                <FiDownload className="text-sm" />
                CSV
              </button>
            </Tooltip>
            <button
              onClick={handleExportPDF}
              disabled={isExportingPDF}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#CC9900] text-white font-bold text-xs shadow-md hover:bg-[#b38600] active:scale-95 disabled:opacity-60 transition-all"
            >
              <FiFileText className={`text-sm ${isExportingPDF ? 'animate-pulse' : ''}`} />
              {isExportingPDF ? 'Generating…' : 'Export PDF'}
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AnalyticsHeader);
